import React, {useEffect, useState} from 'react';
import {ColumnDef, flexRender, getCoreRowModel, useReactTable} from "@tanstack/react-table";
import { useNavigate } from "react-router";
import styles from "./Table.module.scss";

type Orderables = Record<
  // Column ID
  string,
  string
>;
type OrderValue = { direction: 'asc' | 'desc'; key: string };

const colorClassName = ({ orderables, column, order }: { orderables: Orderables, column: { id: string }; order: OrderValue | null }, ascDesc: 'asc' | 'desc') => {
    return orderables[column.id] === order?.key && order?.direction === ascDesc ? 'text-primary-dark' : 'text-dark'
}

export const formats = {
    date: 'date',
    dateTime: 'dateTime',
    fullName: 'fullName',
    length: 'length',
    join: 'join',
    bool: 'bool',
}

export interface TableProps<D extends object> {
    memoisedColumns: ColumnDef<D>[];
    buildLink?: (record: D) => string;
    memoisedData: D[],
}

export default function Table<D extends object>({ memoisedData, memoisedColumns, buildLink }: TableProps<D>) {
    const navigate = useNavigate();
    const table = useReactTable({
        columns: memoisedColumns,
        data: memoisedData,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        // apply the table props
        <div className={styles.TableContainer}>
            <table className={`table table-striped ${styles.Table}`}>
                <thead>
                {// Loop over the header rows
                    table.getHeaderGroups().map((headerGroup) => (
                        // Apply the header row props
                        <tr>
                            {// Loop over the headers in each row
                                headerGroup.headers.map(header => (
                                    // Apply the header cell props
                                    <th>
                                        <small>{flexRender(header.column.columnDef.header, header.getContext())}</small>
                                    </th>
                                ))}
                        </tr>
                    ))}
                </thead>
                {/* Apply the table body props */}
                <tbody>
                {// Loop over the table rows
                    table.getRowModel().rows.map((row) => {
                        // Prepare the row for display
                        return (
                            // Apply the row props
                            <tr
                                onClick={() => {
                                    if (buildLink && row.original) {
                                        const destination = buildLink(row.original)
                                        if (destination) {
                                            if (destination.startsWith('http')) {
                                                // Assume it is an external link, so open it in another tab
                                                window.open(destination, '_blank')?.focus();
                                            } else {
                                                navigate(destination);
                                            }
                                        }
                                    }
                                }}
                            >
                                {// Loop over the row's cells
                                    row.getVisibleCells().map((cell) => {
                                        // Apply the cell props
                                        return <td>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>;
                                    })
                                }
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    );
}
